import React, {
  createContext,
  Dispatch,
  FunctionComponent,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from 'react';
import { ValidationKey } from '../../components/util/validations/types';

export interface ExternalFieldConfigDetails {
  label?: Record<string, string>;
  placeholder?: Record<string, string>;
  validation?: Partial<Record<ValidationKey, any>>;
  errorMessages?: Partial<Record<ValidationKey, any>>;
  [key: string]: any; // additional props
}

export interface ExternalFieldConfig {
  [fieldName: string]: ExternalFieldConfigDetails;
}

export interface ExternalSystemConfig {
  [superiorName: string]: {
    [systemId: string]: {
      dependentNames: string[];
    };
  };
}

export interface ExternalFieldConfigsSource {
  fieldName: string;
  value: string;
}

interface ExternalFieldsConfigContextType {
  externalFieldConfigs: ExternalFieldConfig[] | null;
  externalFieldConfigsSource: ExternalFieldConfigsSource | null;
  setExternalFieldConfigs: (
    configs: ExternalFieldConfig[] | null,
    source?: ExternalFieldConfigsSource
  ) => void;

  externalSystems: ExternalSystemConfig | null;
  setExternalSystems: Dispatch<SetStateAction<ExternalSystemConfig | null>>;

  externalFields: string[];
  setExternalFields: Dispatch<SetStateAction<string[]>>;

  isFetching: boolean;
  setIsFetching: Dispatch<SetStateAction<boolean>>;
}

const ExternalFieldsConfigContext = createContext<
  ExternalFieldsConfigContextType | undefined
>(undefined);

export const useExternalFieldsConfig = () => {
  const context = useContext(ExternalFieldsConfigContext);
  if (!context)
    throw new Error(
      'useExternalFieldsConfig must be used within a ExternalFieldsConfigProvider'
    );
  return context;
};

export const ExternalFieldsConfigProvider: FunctionComponent<{
  children: ReactNode;
}> = ({ children }) => {
  // Kept in one state so the configs and the value they were loaded for
  // always change together
  const [loadedConfigs, setLoadedConfigs] = useState<{
    configs: ExternalFieldConfig[] | null;
    source: ExternalFieldConfigsSource | null;
  }>({ configs: null, source: null });
  const setExternalFieldConfigs = useCallback(
    (
      configs: ExternalFieldConfig[] | null,
      source?: ExternalFieldConfigsSource
    ) => setLoadedConfigs({ configs, source: source ?? null }),
    []
  );
  const [externalSystems, setExternalSystems] =
    useState<ExternalSystemConfig | null>(null);
  const [externalFields, setExternalFields] = useState<string[]>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  return (
    <ExternalFieldsConfigContext.Provider
      value={{
        externalFieldConfigs: loadedConfigs.configs,
        externalFieldConfigsSource: loadedConfigs.source,
        setExternalFieldConfigs,
        externalFields,
        setExternalFields,
        externalSystems,
        setExternalSystems,
        isFetching,
        setIsFetching,
      }}
    >
      {children}
    </ExternalFieldsConfigContext.Provider>
  );
};
